// ─────────────────────────────────────────────────────────────────────────
// microsoftConfig.ts — frontend API client for the Teams integration.
// One row per CIMA instance, managed from Settings → Integrations.
// ─────────────────────────────────────────────────────────────────────────

import { API_BASE, getApiHeaders } from './apiClient';

export type PermissionStatus = 'ok' | 'missing' | 'forbidden' | 'token_failed' | 'warn:missing';
export type PermissionResult = Record<string, PermissionStatus>;

export interface MicrosoftConfigResponse {
  configured:    boolean;
  tenantId?:     string;
  appClientId?:  string;
  secretPreview?: string;
  organiserEmail?: string;
  organiserUserId?: string | null;
  permissionsVerifiedAt?:  string | null;
  permissionsCheckResult?: PermissionResult | null;
  secretExpiresAt?:        string | null;
  lastError?:              string | null;
}

export interface MicrosoftConfigStatus {
  configured: boolean;
  ready:      boolean;
  organiserEmail?: string | null;
  lastError?:      string | null;
  verifiedAt?:     string | null;
  error?:          string;
}

export interface VerifyResult {
  ok:              boolean;
  permissions:     PermissionResult;
  organiserUserId: string | null;
  teamsPremium:    boolean;
  errorMessage:    string | null;
}

export interface SaveInput {
  tenantId:        string;
  appClientId:     string;
  appClientSecret: string;
  organiserEmail:  string;
  secretExpiresAt?: string;
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res  = await fetch(`${API_BASE}${path}`, {
    headers: { ...getApiHeaders(), ...options?.headers },
    ...options,
  });
  const text = await res.text();
  let data: any;
  try { data = JSON.parse(text); } catch { throw new Error(`Server error ${res.status}`); }
  if (!res.ok) {
    const err = new Error(data.error || `Request failed: ${res.status}`);
    (err as any).code   = data.errorCode;
    (err as any).detail = data.detail;
    throw err;
  }
  return data as T;
}

export async function getConfig(): Promise<MicrosoftConfigResponse> {
  return apiFetch<MicrosoftConfigResponse>('/cima/microsoft/config');
}

export async function getStatus(): Promise<MicrosoftConfigStatus> {
  return apiFetch<MicrosoftConfigStatus>('/cima/microsoft/config/status');
}

export async function saveConfig(input: SaveInput): Promise<MicrosoftConfigResponse & { verify: VerifyResult }> {
  return apiFetch('/cima/microsoft/config', {
    method: 'POST',
    body:   JSON.stringify(input),
  });
}

export async function verifyConfig(): Promise<VerifyResult> {
  return apiFetch<VerifyResult>('/cima/microsoft/config/verify', { method: 'POST' });
}

export async function disconnectConfig(): Promise<void> {
  await apiFetch('/cima/microsoft/config', { method: 'DELETE' });
}
